import { useHandler } from "@chatbotgang/etude/react/useHandler";
import type { WebChatConfig } from "@chatbotgang/web-sdk-core/model";
import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import { theme } from "@polifonia/theme";
import { isNil } from "es-toolkit";
import { useMemo } from "react";
import type { Path } from "react-hook-form";
import { Controller, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@/components/Accordion";
import { InfoBox } from "@/components/InfoBox";
import { MotifIcon } from "@/components/MotifIcon";
import { Switch } from "@/components/Switch";
import { Typography } from "@/components/Typography";
import { defineStyles } from "@/internal/emotion";
import { useWebChatConfigForm } from "@/pages/channels/hooks/form/useWebChatConfigForm";
import type { WebChatConfigPage } from "@/pages/channels/pages/detail/wccs/WebChatConfig";

const MAX_PRIMARY_MESSAGE_LENGTH = 20;
const MAX_SECONDARY_MESSAGE_LENGTH = 30;
const MAX_DESCRIPTION_LENGTH = 150;

const styles = defineStyles({
  section: css({
    backgroundColor: theme.colors.white,
    borderRadius: "8px",
  }),
  sectionTitle: css({
    fontWeight: 500,
  }),
  fieldRow: css({
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }),
  fieldColumn: css({
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  }),
  charCount: css({
    color: theme.colors.neutral050,
    textAlign: "right",
  }),
  languageTabs: css({
    marginBottom: "16px",
  }),
});

namespace WelcomeMessagesForm {
  export interface Props {
    selectedLocale: WebChatConfigPage.LocaleKey;
    setSelectedLocale: (locale: WebChatConfigPage.LocaleKey) => void;
  }
}

export const WelcomeMessagesForm: React.FC<WelcomeMessagesForm.Props> =
  function WelcomeMessagesForm({ selectedLocale, setSelectedLocale }) {
    const { t } = useTranslation();
    const { control, clearErrors, setValue, watch } =
      useFormContext<WebChatConfig>();

    const localeLabels: Record<WebChatConfigPage.LocaleKey, string> = useMemo(
      () => ({
        "en-US": t("webChatConfig.welcomeMessages.en"),
        "zh-TW": t("webChatConfig.welcomeMessages.zh"),
        ja: t("webChatConfig.welcomeMessages.ja"),
        th: t("webChatConfig.welcomeMessages.th"),
      }),
      [t],
    );

    const watchedWelcomeMessages = watch(`welcomeMessages.${selectedLocale}`);

    const handleResetPrimaryMessage = useHandler(() => {
      const defaultValue =
        useWebChatConfigForm.defaultWebChatConfig.welcomeMessages[
          selectedLocale
        ].primaryMessageText;
      const field: Path<WebChatConfig> = `welcomeMessages.${selectedLocale}.primaryMessageText`;
      setValue(field, defaultValue);
      clearErrors(field);
    });

    const handleResetSecondaryMessage = useHandler(() => {
      const defaultValue =
        useWebChatConfigForm.defaultWebChatConfig.welcomeMessages[
          selectedLocale
        ].secondaryMessageText;
      const field: Path<WebChatConfig> = `welcomeMessages.${selectedLocale}.secondaryMessageText`;
      setValue(field, defaultValue);
      clearErrors(field);
    });

    const handleResetDescription = useHandler(() => {
      const defaultValue =
        useWebChatConfigForm.defaultWebChatConfig.welcomeMessages[
          selectedLocale
        ].descriptionText;
      const field: Path<WebChatConfig> = `welcomeMessages.${selectedLocale}.descriptionText`;
      setValue(field, defaultValue);
      clearErrors(field);
    });

    const handleResetButtonText = useHandler(() => {
      const defaultValue =
        useWebChatConfigForm.defaultWebChatConfig.welcomeMessages[
          selectedLocale
        ].startChatButtonText;
      const field: Path<WebChatConfig> = `welcomeMessages.${selectedLocale}.startChatButtonText`;
      setValue(field, defaultValue);
      clearErrors(field);
    });

    return (
      <Accordion>
        <AccordionSummary>
          <Typography variant="h3" css={styles.sectionTitle}>
            {t("webChatConfig.welcomeMessages.title")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack css={styles.section} gap="16px">
            <InfoBox
              variant="primary"
              description={
                <Trans i18nKey="webChatConfig.welcomeMessages.infoBox" />
              }
            />
            <Tabs
              value={selectedLocale}
              onChange={(_, newValue) => setSelectedLocale(newValue)}
              css={styles.languageTabs}
            >
              {(
                Object.keys(localeLabels) as Array<WebChatConfigPage.LocaleKey>
              ).map((locale) => (
                <Tab key={locale} value={locale} label={localeLabels[locale]} />
              ))}
            </Tabs>
            <Stack gap="16px" key={selectedLocale}>
              <Box css={styles.fieldColumn}>
                <Box css={styles.fieldRow}>
                  <Controller
                    name={`welcomeMessages.${selectedLocale}.primaryMessageEnabled`}
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...(isNil(field.value)
                          ? {}
                          : { checked: field.value })}
                        onChange={(e, checked) => {
                          field.onChange(e, checked);
                          if (!checked) {
                            clearErrors(
                              `welcomeMessages.${selectedLocale}.primaryMessageText`,
                            );
                          }
                        }}
                      />
                    )}
                  />
                  <Typography variant="body">
                    {t("webChatConfig.welcomeMessages.welcomeMessage")}
                  </Typography>
                </Box>
                <Stack direction="row" gap="8px" alignItems="center">
                  <Controller
                    name={`welcomeMessages.${selectedLocale}.primaryMessageText`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                        placeholder={
                          useWebChatConfigForm.defaultWebChatConfig
                            .welcomeMessages[selectedLocale].primaryMessageText
                        }
                        slotProps={{
                          htmlInput: {
                            maxLength: MAX_PRIMARY_MESSAGE_LENGTH,
                          },
                          input: {
                            endAdornment: (
                              <Typography variant="note" css={styles.charCount}>
                                {watchedWelcomeMessages?.primaryMessageText
                                  .length ?? 0}
                                /{MAX_PRIMARY_MESSAGE_LENGTH}
                              </Typography>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                  <div>
                    <IconButton
                      size="small"
                      onClick={handleResetPrimaryMessage}
                    >
                      <MotifIcon un-i-motif="arrow_refresh" />
                    </IconButton>
                  </div>
                </Stack>
              </Box>
              <Box css={styles.fieldColumn}>
                <Box css={styles.fieldRow}>
                  <Controller
                    name={`welcomeMessages.${selectedLocale}.secondaryMessageEnabled`}
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...(isNil(field.value)
                          ? {}
                          : { checked: field.value })}
                        onChange={(e, checked) => {
                          field.onChange(e, checked);
                          if (!checked) {
                            clearErrors(
                              `welcomeMessages.${selectedLocale}.secondaryMessageText`,
                            );
                          }
                        }}
                      />
                    )}
                  />
                  <Typography variant="body">
                    {t("webChatConfig.welcomeMessages.welcomeSubMessages")}
                  </Typography>
                </Box>
                <Stack direction="row" gap="8px" alignItems="center">
                  <Controller
                    name={`welcomeMessages.${selectedLocale}.secondaryMessageText`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder={
                          useWebChatConfigForm.defaultWebChatConfig
                            .welcomeMessages[selectedLocale]
                            .secondaryMessageText
                        }
                        error={!!error}
                        helperText={error?.message}
                        slotProps={{
                          htmlInput: {
                            maxLength: MAX_SECONDARY_MESSAGE_LENGTH,
                          },
                          input: {
                            endAdornment: (
                              <Typography variant="note" css={styles.charCount}>
                                {watchedWelcomeMessages?.secondaryMessageText
                                  ?.length ?? 0}
                                /{MAX_SECONDARY_MESSAGE_LENGTH}
                              </Typography>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                  <div>
                    <IconButton
                      size="small"
                      onClick={handleResetSecondaryMessage}
                    >
                      <MotifIcon un-i-motif="arrow_refresh" />
                    </IconButton>
                  </div>
                </Stack>
              </Box>
              <Box css={styles.fieldColumn}>
                <Box css={styles.fieldRow}>
                  <Controller
                    name={`welcomeMessages.${selectedLocale}.descriptionEnabled`}
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...(isNil(field.value)
                          ? {}
                          : { checked: field.value })}
                        onChange={(e, checked) => {
                          field.onChange(e, checked);
                          if (!checked) {
                            clearErrors(
                              `welcomeMessages.${selectedLocale}.descriptionText`,
                            );
                          }
                        }}
                      />
                    )}
                  />
                  <Typography variant="body">
                    {t("webChatConfig.welcomeMessages.description")}
                  </Typography>
                </Box>
                <Stack direction="row" gap="8px" alignItems="center">
                  <Controller
                    name={`welcomeMessages.${selectedLocale}.descriptionText`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        error={!!error}
                        helperText={
                          error?.message ?? (
                            <Typography variant="note" css={styles.charCount}>
                              {watchedWelcomeMessages?.descriptionText
                                ?.length ?? 0}
                              /{MAX_DESCRIPTION_LENGTH}
                            </Typography>
                          )
                        }
                        fullWidth
                        multiline
                        rows={3}
                        placeholder={
                          useWebChatConfigForm.defaultWebChatConfig
                            .welcomeMessages[selectedLocale].descriptionText
                        }
                        slotProps={{
                          htmlInput: {
                            maxLength: MAX_DESCRIPTION_LENGTH,
                          },
                          formHelperText: {
                            sx: {
                              textAlign: error ? "left" : "right",
                            },
                          },
                        }}
                      />
                    )}
                  />
                  <div>
                    <IconButton size="small" onClick={handleResetDescription}>
                      <MotifIcon un-i-motif="arrow_refresh" />
                    </IconButton>
                  </div>
                </Stack>
              </Box>
              <Box css={styles.fieldColumn}>
                <Typography variant="body">
                  {t("webChatConfig.welcomeMessages.buttonText")}
                </Typography>
                <Stack direction="row" gap="8px" alignItems="center">
                  <Controller
                    name={`welcomeMessages.${selectedLocale}.startChatButtonText`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                        placeholder={
                          useWebChatConfigForm.defaultWebChatConfig
                            .welcomeMessages[selectedLocale].startChatButtonText
                        }
                      />
                    )}
                  />
                  <div>
                    <IconButton size="small" onClick={handleResetButtonText}>
                      <MotifIcon un-i-motif="arrow_refresh" />
                    </IconButton>
                  </div>
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };
